/** Every navigable view in the application. */
export const SECTION_IDS = ['home', 'tools', 'qa', 'resources', 'gallery', 'contact'] as const;

export type SectionId = (typeof SECTION_IDS)[number];

export interface SectionMeta {
  readonly id: SectionId;
  /** Label shown in the header, mobile menu and footer. */
  readonly label: string;
}

/** Categories an AI tool can belong to; drives the filter row and card icon. */
export type ToolCategory =
  | 'كتابة'
  | 'تصميم'
  | 'عروض'
  | 'صوت'
  | 'فيديو'
  | 'موارد'
  | 'اختبارات'
  | 'تسجيل'
  | 'مواقع'
  | 'تعاون'
  | 'ملاحظات'
  | 'برمجة'
  | 'رسمي';

export interface Tool {
  readonly name: string;
  readonly category: ToolCategory;
  readonly description: string;
  readonly url: string;
  /** Short monogram shown when the remote favicon cannot be loaded. */
  readonly monogram: string;
  /** Domain used to resolve the favicon. */
  readonly domain: string;
}

export interface Resource {
  readonly title: string;
  readonly description: string;
  readonly url: string;
  readonly icon: IconName;
}

export type GalleryType = 'صورة تعليمية' | 'فيديو' | 'رابط تعليمي' | 'مثال عملي';

/** One encoding of a clip that ships with the site. */
export interface VideoSource {
  readonly src: string;
  /** MIME type the browser matches against before it downloads anything. */
  readonly type: string;
}

export interface ShippedClip {
  /** Pixel size of the clip, so the post reserves its shape before it loads. */
  readonly width: number;
  readonly height: number;
  /** Encodings, best-first. */
  readonly sources: readonly VideoSource[];
}

export interface GalleryItem {
  readonly id: string;
  readonly type: GalleryType;
  readonly title: string;
  readonly description: string;
  readonly url: string;
  /** Optional thumbnail URL. */
  readonly thumbnail: string;
  /** Seeded examples ship with the site and cannot be deleted by visitors. */
  readonly seed?: boolean | undefined;
}

export type Role = 'معلم' | 'معلمة' | 'طالب' | 'طالبة';

export interface Reply {
  readonly id: string;
  readonly author: string;
  readonly role: Role;
  /** ISO-8601 timestamp, or `null` for seeded content with a fixed label. */
  readonly createdAt: string | null;
  /** Fixed display label used by seeded content instead of a timestamp. */
  readonly label?: string | undefined;
  readonly body: string;
}

export interface Question {
  readonly id: string;
  readonly author: string;
  readonly role: Role;
  readonly createdAt: string | null;
  readonly label?: string | undefined;
  readonly body: string;
  readonly replies: readonly Reply[];
}

/** Icon keys available from the bundled icon set. */
export type IconName =
  | 'edit'
  | 'grid_view'
  | 'dashboard'
  | 'volume'
  | 'video'
  | 'description'
  | 'check_box'
  | 'schedule'
  | 'language'
  | 'done_all'
  | 'receipt_long'
  | 'code'
  | 'account_balance'
  | 'person'
  | 'star'
  | 'check_circle'
  | 'search'
  | 'mail'
  | 'phone'
  | 'place'
  | 'menu'
  | 'close'
  | 'sun'
  | 'moon';

/** Result of a form validation pass. */
export interface FormFeedback {
  readonly tone: 'success' | 'error';
  readonly message: string;
}
